if (!customElements.get('search-more-results')) {
  /**
   * Tops up the search results page with products its own query does not reach.
   *
   * Two sources, in order:
   *
   *   1. The quick search engine. The page uses /search and the dropdown uses /search/suggest,
   *      and the latter matches more, so the page can look emptier than the dropdown for the
   *      same term.
   *   2. The catalogue index in the browser, for fragments neither engine matches - part of a
   *      word, part of an item number. Same index the dropdown uses.
   *
   * Whatever comes back is filtered against what the page already lists and appended in a
   * block below it. Deliberately kept separate from the filtered grid, so the filter and sort
   * counts stay truthful about the query that produced them. When the page's own query found
   * nothing at all, the "more results" heading is dropped - these are then simply the results.
   */
  class SearchMoreResults extends HTMLElement {
    connectedCallback() {
      // connectedCallback can run more than once if the element is moved in the DOM.
      if (this.loaded) return;
      this.loaded = true;

      this.grid = this.querySelector('[data-more-results-grid]');
      this.terms = this.getAttribute('data-terms');

      if (this.grid && this.terms) this.load();
    }

    /**
     * Collects the handles of the products the search page already lists.
     * @returns {Set<string>}
     */
    // eslint-disable-next-line class-methods-use-this
    getPageHandles() {
      const handles = new Set();

      document.querySelectorAll('#filter-results a[href*="/products/"]').forEach((link) => {
        const [, afterProducts] = link.pathname.split('/products/');
        if (afterProducts) handles.add(afterProducts.split('/')[0]);
      });

      return handles;
    }

    /**
     * The page's own results plus whatever has already been appended, so the two sources below
     * cannot add the same product twice.
     * @returns {Set<string>}
     */
    getShownHandles() {
      const handles = this.getPageHandles();

      this.grid.querySelectorAll('[data-product-handle]').forEach((item) => {
        handles.add(item.dataset.productHandle);
      });

      return handles;
    }

    /**
     * Builds the query string for the products the dropdown would show.
     * @returns {string}
     */
    getParams() {
      return [
        `q=${encodeURIComponent(this.terms)}`,
        `${encodeURIComponent('resources[type]')}=product`,
        // 10 is the most the predictive endpoint will return.
        `${encodeURIComponent('resources[limit]')}=10`,
        `${encodeURIComponent('resources[options][fields]')}=${theme.settings.pSearchFields}`,
        'section_id=search-more-results',
      ].join('&');
    }

    /**
     * Fetches a section and returns the product items in it.
     * @param {string} url - URL to fetch.
     * @returns {Promise<Element[]>}
     */
    // eslint-disable-next-line class-methods-use-this
    async fetchItems(url) {
      const response = await fetch(url);
      if (!response.ok) throw new Error(response.status);

      const tmpl = document.createElement('template');
      tmpl.innerHTML = await response.text();

      return Array.from(tmpl.content.querySelectorAll('[data-product-handle]'));
    }

    /**
     * Appends the items the page is not already showing.
     * @param {Element[]} items
     * @returns {number} How many were added.
     */
    append(items) {
      const shown = this.getShownHandles();
      const missing = items.filter((item) => !shown.has(item.dataset.productHandle));

      missing.forEach((item) => this.grid.appendChild(item));
      // Measured on the page's own grid, not on `shown` - that one already counts the items
      // a previous source appended, so it stops being empty after the first append.
      if (missing.length) this.reveal(this.getPageHandles().size === 0);

      return missing.length;
    }

    /**
     * Shows the block, and squares the rest of the page with it.
     * @param {boolean} pageFoundNothing - Whether the page's own query returned no products.
     */
    reveal(pageFoundNothing) {
      this.hidden = false;

      const noResults = document.querySelector('[data-search-no-results]');
      if (noResults) noResults.hidden = true;

      if (pageFoundNothing) {
        const heading = this.querySelector('[data-more-results-heading]');
        if (heading) heading.hidden = true;
      }
    }

    /**
     * Asks the browser-side index for products the engines missed, and appends their cards.
     */
    async loadFromIndex() {
      if (!window.ZimpleSearchIndex) return;

      const hits = await window.ZimpleSearchIndex.match(this.terms, 12);
      if (!hits.length) return;

      const query = hits.map((hit) => `id:${hit.i}`).join(' OR ');
      const items = await this.fetchItems(
        `${theme.routes.searchUrl}?q=${encodeURIComponent(query)}&type=product&section_id=search-more-results`
      );

      // Back into the index's order: an "id:" query carries no relevance of its own.
      const order = hits.map((hit) => String(hit.i));
      items.sort((a, b) => order.indexOf(a.dataset.productId) - order.indexOf(b.dataset.productId));

      this.append(items);
    }

    /**
     * Runs both sources, in order.
     */
    async load() {
      try {
        this.append(await this.fetchItems(`${theme.routes.predictiveSearch}?${this.getParams()}`));
      } catch (error) {
        // A failed top-up should leave the page exactly as the server rendered it.
      }

      try {
        await this.loadFromIndex();
      } catch (error) {
        // As above: the page is still correct without this.
      }
    }
  }

  customElements.define('search-more-results', SearchMoreResults);
}
